import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { ApolloError } from "@apollo/client";
import { Viewport } from "@urbica/react-map-gl";
import {
  useFullUpdateLocationMutation,
  useGetOrCreateLocationFromCoordinateMutation,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useActionBy } from "@src/common/hooks/useActionBy";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { initialViewport } from "@src/common/constants/mapConstants";
import { CustomLocationResult, LocationFormData, MarkerPosition } from "../types";

interface CustomLocationContextValue {
  // Shared marker state (map -> form coordination)
  markerPosition: MarkerPosition;
  setMarkerPosition: (pos: MarkerPosition) => void;
  hasPinned: boolean;
  setHasPinned: (pinned: boolean) => void;

  // Viewport state (owned by map but shared for initialization)
  viewport: Viewport;
  setViewport: (viewport: Viewport) => void;

  // Drag state
  isDragging: boolean;
  setIsDragging: (dragging: boolean) => void;

  // Submit coordination
  isLoading: boolean;
  submitLocation: (data: LocationFormData) => Promise<void>;

  // Modal control
  onClose: () => void;

  // UI configuration
  submitButtonText: string;
}

const CustomLocationContext = createContext<CustomLocationContextValue | null>(null);

export function useCustomLocation(): CustomLocationContextValue {
  const context = useContext(CustomLocationContext);
  if (!context) {
    throw new Error("useCustomLocation must be used within a CustomLocationProvider");
  }
  return context;
}

interface CustomLocationProviderProps {
  children: React.ReactNode;
  onClose: () => void;
  onSuccess: (location: CustomLocationResult) => void;
  submitButtonText: string;
}

export const CustomLocationProvider: React.FC<CustomLocationProviderProps> = ({
  children,
  onClose,
  onSuccess,
  submitButtonText,
}) => {
  const { courier, courierId } = useAuth();
  const { createdBy } = useActionBy();
  const { showToast } = useCourieStore();

  // Compute default viewport based on courier's primary location
  const defaultViewport = useMemo<Viewport>(() => {
    const primaryLngLat = courier?.courier?.primaryLocation?.lngLat;
    if (primaryLngLat?.lat != null && primaryLngLat?.lng != null) {
      return {
        ...initialViewport,
        latitude: primaryLngLat.lat,
        longitude: primaryLngLat.lng,
        zoom: 12,
      };
    }
    return initialViewport;
  }, [courier?.courier?.primaryLocation?.lngLat]);

  // Shared state
  const [viewport, setViewport] = useState<Viewport>(defaultViewport);
  const [markerPosition, setMarkerPosition] = useState<MarkerPosition>({
    latitude: defaultViewport.latitude,
    longitude: defaultViewport.longitude,
  });
  const [hasPinned, setHasPinned] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Update viewport and marker when courier data loads
  useEffect(() => {
    const primaryLngLat = courier?.courier?.primaryLocation?.lngLat;
    if (primaryLngLat?.lat != null && primaryLngLat?.lng != null && !hasPinned) {
      const lat = primaryLngLat.lat;
      const lng = primaryLngLat.lng;
      setViewport((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
        zoom: 12,
      }));
      setMarkerPosition({
        latitude: lat,
        longitude: lng,
      });
    }
  }, [courier?.courier?.primaryLocation?.lngLat, hasPinned]);

  // Mutations
  const [getOrCreateLocationFromCoordinateMutation, { loading: createLoading }] =
    useGetOrCreateLocationFromCoordinateMutation();

  const [fullUpdateLocationMutation, { loading: updateLoading }] =
    useFullUpdateLocationMutation();

  const isLoading = createLoading || updateLoading;

  const submitLocation = useCallback(
    async (data: LocationFormData) => {
      if (!courierId || !hasPinned) return;

      try {
        const createResult = await getOrCreateLocationFromCoordinateMutation({
          variables: {
            courierId,
            lngLat: {
              lng: markerPosition.longitude,
              lat: markerPosition.latitude,
            },
            createdBy,
          },
        });

        const createdLocation =
          createResult?.data?.getOrCreateLocationFromCoordinate?.location;

        if (!createdLocation) {
          showToast({
            type: "error",
            message: "Failed to create location from coordinates",
          });
          return;
        }

        const updateResult = await fullUpdateLocationMutation({
          variables: {
            courierId,
            id: createdLocation.id,
            streetAddress: data.streetAddress,
            city: data.city,
          },
        });

        if (updateResult?.data?.fullUpdateLocation) {
          const updatedLocation = updateResult.data.fullUpdateLocation.location;

          if (updatedLocation) {
            showToast({
              type: "success",
              message: "Location created successfully",
            });

            onSuccess({
              locationId: updatedLocation.id,
              streetAddress: data.streetAddress,
              city: data.city,
              state: updatedLocation.state || createdLocation.state || "",
              country: updatedLocation.country || createdLocation.country || "",
              zipCode: updatedLocation.zipCode || createdLocation.zipCode || "",
              lngLat: {
                lng: markerPosition.longitude,
                lat: markerPosition.latitude,
              },
            });

            onClose();
          }
        }
      } catch (error) {
        showErrorToast(error as ApolloError, showToast);
      }
    },
    [
      courierId,
      createdBy,
      hasPinned,
      markerPosition,
      getOrCreateLocationFromCoordinateMutation,
      fullUpdateLocationMutation,
      showToast,
      onSuccess,
      onClose,
    ]
  );

  const value: CustomLocationContextValue = useMemo(
    () => ({
      markerPosition,
      setMarkerPosition,
      hasPinned,
      setHasPinned,
      viewport,
      setViewport,
      isDragging,
      setIsDragging,
      isLoading,
      submitLocation,
      onClose,
      submitButtonText,
    }),
    [
      markerPosition,
      hasPinned,
      viewport,
      isDragging,
      isLoading,
      submitLocation,
      onClose,
      submitButtonText,
    ]
  );

  return (
    <CustomLocationContext.Provider value={value}>
      {children}
    </CustomLocationContext.Provider>
  );
};
